import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, FileDown, GraduationCap, AlertCircle } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const AdmissionReport = () => {
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState({ total: 0, byCourse: {}, byGender: { Male: 0, Female: 0, Other: 0 }, totalCourseFee: 0, totalPaid: 0, totalBalance: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchReport();
    fetchCollegeInfo();
  }, [currentPage, itemsPerPage]);

  const fetchCollegeInfo = async () => {
    try {
      const response = await apiClient.get('/api/tenants/my-college/settings');
      setCollegeInfo(response.data);
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const getPaid = (s) => (s.admission_fee || 0) + (s.seat_booking_amount || s.booking_amount || 0);
  const getBalance = (s) => s.balance_fee != null ? s.balance_fee : ((s.total_course_fee || 0) - getPaid(s) - (s.discount_amount || 0) - (s.scholarship_amount || 0));

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const response = await apiClient.get(`/api/students/?${params.toString()}&include_docs=false`);
      
      let studentsData = [];
      let total = 0;
      
      if (Array.isArray(response.data)) {
        studentsData = response.data;
        total = response.data.length;
      } else {
        studentsData = response.data?.data || [];
        total = response.data?.total || 0;
      }
      
      setStudents(studentsData);
      setTotalItems(total);
      
      const byCourse = studentsData.reduce((acc, s) => {
        const course = s.course_name || 'Unknown';
        acc[course] = (acc[course] || 0) + 1;
        return acc;
      }, {});
      
      const byGender = studentsData.reduce((acc, s) => {
        const gender = s.gender || 'Other';
        acc[gender] = (acc[gender] || 0) + 1;
        return acc;
      }, { Male: 0, Female: 0, Other: 0 });

      const totalCourseFee = studentsData.reduce((sum, s) => sum + (s.total_course_fee || 0), 0);
      const totalPaid = studentsData.reduce((sum, s) => sum + getPaid(s), 0);
      const totalBalance = studentsData.reduce((sum, s) => sum + Math.max(0, getBalance(s)), 0);
      
      setSummary({ total, byCourse, byGender, totalCourseFee, totalPaid, totalBalance });
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load data. Please try again.');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '1000');
      
      const response = await apiClient.get(`/api/students/?${params.toString()}&include_docs=false`);
      
      let allStudents = [];
      if (Array.isArray(response.data)) {
        allStudents = response.data;
      } else {
        allStudents = response.data?.data || [];
      }
      
      if (allStudents.length === 0) { alert('No data to export'); return; }

      const totalCourseFee = allStudents.reduce((sum, s) => sum + (s.total_course_fee || 0), 0);
      const totalPaid = allStudents.reduce((sum, s) => sum + getPaid(s), 0);
      const totalBalance = allStudents.reduce((sum, s) => sum + Math.max(0, getBalance(s)), 0);

      const stats = {
        'Total Admissions': allStudents.length,
        'Total Course Fee': `Rs. ${totalCourseFee.toLocaleString('en-IN')}`,
        'Total Collected': `Rs. ${totalPaid.toLocaleString('en-IN')}`,
        'Total Pending': `Rs. ${totalBalance.toLocaleString('en-IN')}`
      };

      const columns = [
        { header: 'Adm #', dataKey: 'admission_number' },
        { header: 'Student Name', dataKey: 'full_name' },
        { header: 'Course', dataKey: 'course_name' },
        { header: 'Session', dataKey: 'session' },
        { header: 'Course Fee', dataKey: 'course_fee' },
        { header: 'Paid', dataKey: 'paid' },
        { header: 'Balance', dataKey: 'balance' },
        { header: 'Status', dataKey: 'status' }
      ];

      const data = allStudents.map(s => ({
        admission_number: s.admission_number || 'N/A',
        full_name: s.full_name || 'N/A',
        course_name: s.course_name || 'N/A',
        session: s.session || 'N/A',
        course_fee: `Rs. ${(s.total_course_fee || 0).toLocaleString('en-IN')}`,
        paid: `Rs. ${getPaid(s).toLocaleString('en-IN')}`,
        balance: `Rs. ${Math.max(0, getBalance(s)).toLocaleString('en-IN')}`,
        status: s.is_approved ? 'Approved' : 'Pending'
      }));

      exportSummaryReportToPDF('Admission Report', stats, columns, data, 'admission_report.pdf', collegeInfo);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            Admission Report
          </CardTitle>
          <div className="flex gap-4 items-end">
            <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
            <div><Label>End Date</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
            <Button onClick={fetchReport} disabled={loading}><FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Refresh'}</Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={students.length === 0 || exporting}>
              <FileDown className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          {/* Summary Cards - Financial Overview */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-600">Total Students</p>
              <p className="text-2xl font-bold text-blue-700">{summary.total}</p>
            </div>
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="text-xs text-indigo-600">Courses</p>
              <p className="text-2xl font-bold text-indigo-700">{Object.keys(summary.byCourse).length}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-600">Male</p>
              <p className="text-2xl font-bold text-green-700">{summary.byGender.Male}</p>
            </div>
            <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
              <p className="text-xs text-pink-600">Female</p>
              <p className="text-2xl font-bold text-pink-700">{summary.byGender.Female}</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-600">Total Collected</p>
              <p className="text-xl font-bold text-emerald-700">₹{summary.totalPaid.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-xs text-red-600">Total Pending</p>
              <p className="text-xl font-bold text-red-700">₹{summary.totalBalance.toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* Course Breakdown */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Admissions by Course</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(summary.byCourse).map(([courseName, count]) => (
                <div key={courseName} className="p-3 border rounded-lg bg-gradient-to-r from-blue-50 to-purple-50">
                  <p className="text-sm text-gray-600 truncate" title={courseName}>{courseName}</p>
                  <p className="text-lg font-bold text-blue-700">{count} students</p>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <h3 className="text-lg font-semibold mb-3">Student Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3">Admission #</th>
                  <th className="text-left p-3">Student Name</th>
                  <th className="text-left p-3">Course</th>
                  <th className="text-left p-3">Session</th>
                  <th className="text-right p-3">Course Fee</th>
                  <th className="text-right p-3">Paid</th>
                  <th className="text-right p-3">Balance</th>
                  <th className="text-left p-3">Admission Date</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" className="text-center p-8 text-gray-500">Loading...</td></tr>
                ) : students.length === 0 ? (
                  <tr><td colSpan="9" className="text-center p-8 text-gray-500">No admission records found.</td></tr>
                ) : (
                  students.map((student, idx) => {
                    const paid = getPaid(student);
                    const balance = Math.max(0, getBalance(student));
                    return (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{student.admission_number}</td>
                        <td className="p-3">{student.full_name}</td>
                        <td className="p-3"><span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">{student.course_name || 'N/A'}</span></td>
                        <td className="p-3 text-sm text-gray-600">{student.session || 'N/A'}</td>
                        <td className="p-3 text-right font-semibold">₹{(student.total_course_fee || 0).toLocaleString('en-IN')}</td>
                        <td className="p-3 text-right font-semibold text-green-700">₹{paid.toLocaleString('en-IN')}</td>
                        <td className={`p-3 text-right font-semibold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ₹{balance.toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-sm">{student.admission_date || 'N/A'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${student.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {student.is_approved ? 'Approved' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / itemsPerPage)}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdmissionReport;
